import { FC, useEffect, useMemo, useState } from 'react'

import { TableSortType } from 'src/components/molecules'
import {
  ExternalUserListTable,
  ExternalUserSortItemType,
  Pagination,
} from 'src/components/organisms'

import { UsersPerPage } from 'src/constants'
import { useUser } from 'src/hooks'
import { ExternalUserEntity } from 'src/types'

type Props = {
  externalUsers: ExternalUserEntity[]
  searchWord?: string
  updateDisplayInternalUserCount?: (count: number) => void
  className?: string
}

export const ExternalUserList: FC<Props> = ({
  externalUsers,
  searchWord,
  updateDisplayInternalUserCount,
  className,
}) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [sortItem, setSortItem] = useState<ExternalUserSortItemType>('id')
  const [sortType, setSortType] = useState<TableSortType>('asc')

  const { searchUsers } = useUser()

  const filteredUsers = useMemo(() => {
    return searchWord
      ? searchUsers<ExternalUserEntity>({ users: externalUsers, searchWord })
      : externalUsers
  }, [searchWord, searchUsers, externalUsers])

  const sortedUsers = useMemo(() => {
    const order = sortType === 'desc' ? 1 : -1

    if (sortItem === 'name') {
      return [...filteredUsers].sort((a, b) => {
        return (a.name ?? '-') < (b.name ?? '-') ? order : -order
      })
    }

    if (sortItem === 'keywords') {
      return [...filteredUsers].sort((a, b) => {
        const aKeyword = a.keywords.length > 0 ? a.keywords.join(',') : '-'
        const bKeyword = b.keywords.length > 0 ? b.keywords.join(',') : '-'

        // localeCompareはsortの昇順に合わせて -1,0,1 を返すので、降順の場合は逆転させる
        const compareValue = aKeyword.localeCompare(bKeyword, 'ja-JP')
        return sortType === 'asc' ? compareValue : -compareValue
      })
    }

    return [...filteredUsers].sort((a, b) => {
      return a[sortItem]! < b[sortItem]! ? order : -order
    })
  }, [filteredUsers, sortItem, sortType])

  const displayUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * UsersPerPage
    const endIndex = startIndex + UsersPerPage
    return sortedUsers.slice(startIndex, endIndex)
  }, [sortedUsers, currentPage])

  useEffect(() => {
    updateDisplayInternalUserCount && updateDisplayInternalUserCount(displayUsers.length)
  }, [displayUsers, updateDisplayInternalUserCount])

  // 検索ワードが変わった時1ページ目に戻る
  useEffect(() => setCurrentPage(1), [searchWord])

  const totalPage = useMemo(() => Math.ceil(filteredUsers.length / UsersPerPage), [filteredUsers])

  return (
    <div className={className}>
      <ExternalUserListTable
        externalUsers={displayUsers}
        sortItem={sortItem}
        sortType={sortType}
        selectSortItem={setSortItem}
        selectSortType={setSortType}
        afterOnSort={() => setCurrentPage(1)}
      />
      {totalPage > 1 && (
        <Pagination
          totalPage={totalPage}
          currentPage={currentPage}
          select={setCurrentPage}
          className="tw-mt-s0"
        />
      )}
    </div>
  )
}
